// 一覧（トップ）。2026-10-23 から CrazyGames の並べ方（theme.css の頭）：
// 続きから（小さな正方形の列）→ 今日のおすすめ（大 1・小 4・大 1… を組み合わせたタイル）→ 種類ごとの列 → すべてのゲーム → 広告の枠。
// ゲームの絵は正方形で出す（2026-10-23 ちさとさん「サムネイルは一旦正方形に。今の表示だと見切れてる」）。
// 絞り込み（札・検索）をしているときは、札と見つかったゲームだけを出す。読み込みのあいだは、形だけの灰色の骨組みを出す。
import { loadCatalog, type CatalogGame } from "../catalog";
import { getLocale, pick, t, tEn, type MessageKey } from "../i18n";
import introJa from "../home-intro-ja.json";
import { recentGames } from "../local";
import { h } from "./dom";
import { getFilter, onFilter, setFilter, type Filter } from "./filter";
import { icon } from "./icons";
import { adSlot, gamePath, gameTile, hoverPreview } from "./parts";
import type { View } from "./view";

export function matches(g: CatalogGame, f: Filter): boolean {
  if (f.chip === "quick" && g.minutes[1] > 1) return false;
  if (f.chip.startsWith("genre:") && g.genre !== f.chip.slice(6)) return false;
  if (f.chip.startsWith("subject:") && g.subject !== f.chip.slice(8)) return false;
  const q = f.q.trim().toLowerCase();
  if (!q) return true;
  const l = getLocale();
  const hay = [g.title[l], g.hook[l], g.description[l], t(`genre.${g.genre}` as MessageKey), g.subject ? t(`subject.${g.subject}` as MessageKey) : ""];
  return hay.join(" ").toLowerCase().includes(q);
}

/** 絞り込みの札の中身：すべて・1分で遊べる・種類・教科（ゲームがある物だけ）。key は文言の鍵（見出しの英字に使う） */
export function categories(games: CatalogGame[]): { id: string; key: MessageKey; label: string }[] {
  const genres = [...new Set(games.map((g) => g.genre))];
  const subjects = [...new Set(games.map((g) => g.subject).filter((s): s is NonNullable<typeof s> => !!s))];
  const keys: { id: string; key: MessageKey }[] = [
    { id: "all", key: "chip.all" },
    ...(games.some((g) => g.minutes[1] <= 1) ? [{ id: "quick", key: "chip.quick" as MessageKey }] : []),
    ...genres.map((g) => ({ id: `genre:${g}`, key: `genre.${g}` as MessageKey })),
    ...subjects.map((s) => ({ id: `subject:${s}`, key: `subject.${s}` as MessageKey })),
  ];
  return keys.map((c) => ({ ...c, label: t(c.key) }));
}

/** 見出し：LP の ABOUT と同じ丸くふくらんだ英字に、日本語を小さく添える（英語の画面では英字だけ）。
 *  押すとその種類で絞り込む物は、右に › を付ける */
function heading(key: MessageKey, opts: { chip?: string; tag?: "h1" | "h2"; sub?: string } = {}): HTMLElement {
  const ja = getLocale() !== "en";
  const inner = [
    h("span", { class: "mk", "aria-hidden": ja ? "true" : undefined }, tEn(key)),
    ja ? h("span", { class: "h2-j" }, t(key)) : "",
    opts.chip ? h("span", { class: "chev", "aria-hidden": "true" }, "›") : "",
  ];
  return h(
    opts.tag ?? "h2",
    { class: "h2" },
    ...(opts.chip ? [h("button", { class: "h2-link", type: "button", onclick: () => setFilter({ chip: opts.chip! }) }, ...inner)] : inner),
    opts.sub ? h("span", { class: "h2-s ph" }, opts.sub) : "",
  );
}

/** 続きから：この端末で遊んだゲームを、小さな正方形の列で（CrazyGames の「ゲームを続ける」） */
function continueRow(games: CatalogGame[]): HTMLElement | null {
  const byId = new Map(games.map((g) => [g.id, g]));
  const recent = recentGames()
    .map((r) => byId.get(r.id))
    .filter((g): g is CatalogGame => !!g)
    .slice(0, 12);
  if (!recent.length) return null;
  return h(
    "section",
    { class: "blk", id: "continue" },
    heading("home.continue"),
    h(
      "div",
      { class: "rail rail-s" },
      ...recent.map((g) => gameTile(g, { testid: `continue-${g.id}`, extra: h("span", { class: "gt-play", "aria-hidden": "true" }, icon("play")) })),
    ),
  );
}

/** 今日のおすすめ：大きな 1 本・小さな 4 本（2×2）を交互に並べ、横に流す。大きなタイルは、絵の上に題名・ひとこと・「遊ぶ」を重ねる */
function picks(list: CatalogGame[]): HTMLElement {
  const items = list.slice(0, 10).map((g, i) => {
    if (i % 5 !== 0) return gameTile(g, { testid: `game-card-${g.id}` });
    return hoverPreview(h(
      "a",
      { class: "gt big", href: gamePath(g), "data-testid": `game-card-${g.id}` },
      h("img", { src: g.thumbnail, alt: "", width: 512, height: 512, decoding: "async", loading: i === 0 ? "eager" : "lazy" }),
      h(
        "span",
        { class: "big-bar" },
        h("img", { class: "big-ic", src: g.thumbnail, alt: "", width: 512, height: 512, loading: "lazy" }),
        h("span", { class: "big-tx" }, h("span", { class: "big-t ph" }, pick(g.title)), h("span", { class: "big-h ph" }, pick(g.hook))),
        h("span", { class: "big-play" }, icon("play"), t("play.play")),
      ),
    ), g);
  });
  return h("div", { class: "picks" }, ...items);
}

/** 読み込みのあいだの骨組み（形だけ・灰色。光が流れる） */
function skeleton(): HTMLElement[] {
  const bones = (n: number, cls: (i: number) => string) => Array.from({ length: n }, (_, i) => h("span", { class: `bone ${cls(i)}` }));
  return [
    h("section", { class: "blk blk-picks", "aria-busy": "true" }, h("span", { class: "bone bone-h" }), h("div", { class: "picks" }, ...bones(6, (i) => (i % 5 === 0 ? "gt big" : "gt")))),
  ];
}

/** ゲームの一覧の後で、サイトの説明と案内を読めるようにする */
function homeIntro(): HTMLElement {
  return h(
    "section",
    { class: "blk note-blk", "aria-labelledby": "home-intro-title" },
    h("h2", { class: "h2 ph", id: "home-intro-title" }, t("home.introTitle")),
    ...t("home.introBody").split(/\n\s*\n/).filter(Boolean).map((p) => {
      const phrases = getLocale() === "ja" ? introJa.find((parts) => parts.join("") === p) : undefined;
      return h("p", { class: "ph" }, ...(phrases ? phrases.flatMap((part, i) => i ? [h("wbr"), part] : [part]) : [p]));
    }),
    h("p", {}, h("a", { href: "/about/", target: "_self" }, t("home.introLink"))),
  );
}

export function homeView(): View {
  const el = h("div", { class: "wrap home" }, ...skeleton());
  document.title = t("meta.title");
  let games: CatalogGame[] | null = null;

  const render = () => {
    if (!games) return;
    const f = getFilter();
    const filtered = f.q.trim() !== "" || f.chip !== "all";
    const list = games.filter((g) => matches(g, f));
    if (filtered) {
      el.replaceChildren(
        h(
          "section",
          { class: "blk" },
          // カテゴリーだけで絞ったときは、その名前を見出しにする（PC は左のメニューで選ぶので、何を見ているかを出す）
          heading((!f.q.trim() && categories(games).find((c) => c.id === f.chip)?.key) || "home.results", { tag: "h1" }),
          list.length ? h("div", { class: "grid" }, ...list.map((g) => gameTile(g))) : h("p", { class: "empty" }, t("home.noMatch")),
        ),
        homeIntro(),
      );
      return;
    }
    if (!list.length) {
      el.replaceChildren(h("p", { class: "empty" }, t("home.empty")), homeIntro());
      return;
    }
    // 2 本以上ある種類だけ、列にする（1 本だけの列は寂しく見えるので、すべてのゲームで見せる）
    const rows = categories(games)
      .filter((c) => c.id !== "all")
      .map((c) => ({ ...c, games: games!.filter((g) => matches(g, { q: "", chip: c.id })) }))
      .filter((c) => c.games.length >= 2);
    el.replaceChildren(
      continueRow(games) || "",
      h("section", { class: "blk blk-picks" }, heading("home.featured", { tag: "h1", sub: t("home.featuredSub") }), picks(list)),
      ...rows.map((c) => h("section", { class: "blk" }, heading(c.key, { chip: c.id }), h("div", { class: "rail" }, ...c.games.map((g) => gameTile(g))))),
      adSlot("home", "ad-wide"),
      h("section", { class: "blk" }, heading("home.all"), h("div", { class: "grid" }, ...list.map((g) => gameTile(g)))),
      homeIntro(),
    );
  };

  const off = onFilter(render);
  loadCatalog()
    .then((list) => {
      games = list;
      render();
    })
    .catch((e) => {
      console.error(e);
      el.replaceChildren(h("p", { class: "empty" }, t("error.generic")));
    });
  return { el, destroy: off };
}
